import { BridgeAppraisalStatus } from '../../../../../../../common/constants/BridgeAppraisalStatus';

export class BridgeAppraisalStatusGroupMapper {
    /**
     * Enum-like grouping sections.
     * Centralized numeric ordering + identity.
     */
    static Section = {
        Initial: 0,
        BfiToOrder: 1,
        Ordered: 2,
        Appraised: 3,
        ApprovedWaitingToClose: 4,
        Closed: 5,
    } as const;

    /**
     * Centralized labels tied directly to the Section enum.
     */
    static BaseLabels: Record<number, string> = {
        [this.Section.Initial]: 'Initial',
        [this.Section.BfiToOrder]: 'BFI To Order',
        [this.Section.Ordered]: 'Ordered',
        [this.Section.Appraised]: 'Appraised',
        [this.Section.ApprovedWaitingToClose]: 'Approved / Waiting To Close',
        [this.Section.Closed]: 'Closed',
    };

    /**
     * Descriptor labels tied directly to the Section enum.
     */
    static DescriptorText: Record<number, string> = {
        [this.Section.BfiToOrder]: 'key items satisfactory',
        [this.Section.Appraised]: 'received and review complete',
    };

    /**
     * Dynamic Labels (no helper)
     */
    static getLabels(capitalizeLabel: boolean): Record<number, string> {
        return Object.fromEntries(
            Object.entries(this.BaseLabels).map(([key, base]) => [key, capitalizeLabel ? base.toUpperCase() : base])
        );
    }

    /**
     * Dynamic LabelsWithHelper (base + helper)
     */
    static getLabelsWithDescription(capitalizeLabel: boolean): Record<number, string> {
        return Object.fromEntries(
            Object.entries(this.BaseLabels).map(([key, base]) => {
                const helper = this.DescriptorText[Number(key)];
                const full = helper ? `${base} (${helper})` : base;
                return [key, capitalizeLabel ? full.toUpperCase() : full];
            })
        );
    }

    /**
     * Returns the numeric group key used for sorting and grouping.
     * Values 0–3 collapse into bucket 0.
     */
    static getGroupSection(status: BridgeAppraisalStatus): number | undefined {
        const preAppraisalStatuses = [
            BridgeAppraisalStatus.NothingReceived,
            BridgeAppraisalStatus.PaymentReceived,
            BridgeAppraisalStatus.SurveyReceived,
            BridgeAppraisalStatus.PaymentAndSurveyReceived,
        ];

        if (preAppraisalStatuses.includes(status)) {
            return this.Section.Initial;
        }

        switch (status) {
            case BridgeAppraisalStatus.ReadyForAppraisal:
                return this.Section.BfiToOrder;
            case BridgeAppraisalStatus.AppraisalOrdered:
                return this.Section.Ordered;
            case BridgeAppraisalStatus.Appraised:
                return this.Section.Appraised;
            case BridgeAppraisalStatus.ApprovedAndWaitingToClose:
                return this.Section.ApprovedWaitingToClose;
            case BridgeAppraisalStatus.Closed:
                return this.Section.Closed;
            default:
                return undefined;
        }
    }

    /**
     * Returns the display label for a given group key.
     * Safe for undefined.
     */
    static getGroupLabel(section: number | undefined, capitalizeLabel = false): string {
        if (section === undefined) return '';
        return this.getLabels(capitalizeLabel)[section] ?? '';
    }

    static getGroupLabelsWithDescription(section: number | undefined, capitalizeLabel = false): string {
        if (section === undefined) return '';
        return this.getLabelsWithDescription(capitalizeLabel)[section] ?? '';
    }
}

export type BridgeAppraisalStatusGroupSection =
    (typeof BridgeAppraisalStatusGroupMapper.Section)[keyof typeof BridgeAppraisalStatusGroupMapper.Section];
